import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  AppShellTrigger,
  Box,
  ButtonExpansion,
  ControlBar,
  ControlBarButton,
  Group,
  Icon,
  Separator,
  Text,
  useIsMobile,
  useIsTouchDevice,
  type Omittable,
} from "@salestrak/ui";

import { EntityTag } from "#app/components/entity";
import { FilterPanel } from "#app/components/filters/filter-panel";
import { useAppData } from "#app/lib/data";
import type { FilterConfig, UseFiltersResult } from "#app/lib/filters/types";
import { OPTIONS_PANEL_TRIGGER_PROPS } from "#app/components/view-options/options-panel-slots";
import { MapSearchBar } from "./map-search-bar";
import { useClickAwayPassThrough } from "./use-click-away-pass-through";
import {
  MAP_CENTER_SHORTCUT,
  MAP_ZOOM_IN_SHORTCUT,
  MAP_ZOOM_OUT_SHORTCUT,
} from "./map-shortcuts";
import { MIN_ZOOM, MAX_ZOOM, type MapShade } from "./map-styles";
import { useMapTileTheme } from "./use-map-tile-theme";

// Safe-area insets stay in CSS so the controls react to viewport changes
// without a JavaScript measurement pass. Desktop anchors to the rail's 32px
// workspace tile band (16px from the viewport top; the page card's border
// eats 1px, so 7px inside the card) — the search and both clusters are 32px
// tall, so they share that band edge-for-edge. Mobile keeps the 16px touch
// inset.
const TOP_INSET =
  "top-[calc(env(safe-area-inset-top,0px)+1rem)] sm:top-[calc(env(safe-area-inset-top,0px)+7px)]";
// The controls column is an inline-size container. Under 360px the visible
// map (right of an open drawer, beside an open module menu) cannot hold a
// readable search field beside the options cluster — 220px of field + 8px
// gap + the cluster at its widest (three segments and a filter count) ≈
// 350 — so the field folds to one glyph button on the same 32px rung and
// expands over the whole row on press (the toolbars' ExpandableSearch
// grammar); the cluster steps aside only while the field is open. Hiding
// the controls outright was tried and rejected: a sliver of map is still
// the map, and its tools should be where they always are.
const CONTROLS_CONTAINER_CLASS = "@container/map-controls";
const NARROW_HIDDEN_CLASS = "@max-[360px]/map-controls:hidden";
const WIDE_HIDDEN_CLASS = "@min-[361px]/map-controls:hidden";
const MOBILE_SECOND_ROW_INSET =
  "top-[calc(env(safe-area-inset-top,0px)+4.875rem)]";

type MapOverlayControlsProps = Omittable<{
  /** The basemap's shade — the control fills derive from the tiles. */
  mapShade?: MapShade;
  /** Open right-side chrome shifts the options dock into the visible map. */
  rightInset?: number;
  /** The filter panel, which the dock renders with the filter action as its
   *  trigger. A trigger renders in place — no wrapper, so the joined geometry
   *  holds — and only a menu with a registered trigger can host the panel's
   *  hover submenus (see `FilterPanel`). */
  filterConfig: FilterConfig;
  filters: UseFiltersResult;
  filterPanelOpen: boolean;
  onFilterPanelOpenChange: (open: boolean) => void;
  // Avatar toggle
  currentUserSelected: boolean;
  onCurrentUserClick: () => void;
  onCurrentUserClear: () => void;
  // Zoom
  onZoomIn: () => void;
  onZoomOut: () => void;
  zoomLevel: number;
  /** Center on the viewer's location. Absent while the browser has delivered
   *  no fix — the action is not rendered rather than shown dead; the map
   *  screen's persistent toast explains why and carries the retry. */
  onLocationClick?: () => void;
  // Search
  onSearchLocationSelect: (
    coords: { latitude: number; longitude: number },
    addressId?: string,
  ) => void;
  onSearchProjectSelect: (projectId: string) => void;
  // Combined map and project-card display options
  displayOptionsOpen: boolean;
  onDisplayOptionsClick: () => void;
}>;

// Memoized: the map screen re-renders on each selection and URL write, and
// none of those change the overlay.
export const MapOverlayControls = memo(function MapOverlayControls({
  mapShade,
  rightInset = 0,
  filterConfig,
  filters,
  filterPanelOpen,
  onFilterPanelOpenChange,
  currentUserSelected,
  onCurrentUserClick,
  onCurrentUserClear,
  onZoomIn,
  onZoomOut,
  zoomLevel,
  onLocationClick,
  onSearchLocationSelect,
  onSearchProjectSelect,
  displayOptionsOpen,
  onDisplayOptionsClick,
}: MapOverlayControlsProps) {
  // Control colors come from the tile theme, not a token rung: they carry a
  // guaranteed polarity step off the tile ground plus a hairline the imagery
  // cannot wash out (see MapTileTheme).
  const { controlBg, controlBorder } = useMapTileTheme(mapShade);
  const { currentUser } = useAppData();
  const isMobile = useIsMobile();
  // Zoom buttons are a pointer-and-keyboard affordance. A touch-primary
  // device zooms by pinch and double-tap — the grammar every mapping app
  // ships — so the buttons would only hold map real estate beside the top
  // island. The gate is the pointer, never the viewport: a touch laptop
  // with a mouse, or a tablet with a trackpad, keeps them. The shortcuts
  // stay wired either way, and Center on my location stays in the column.
  const isTouch = useIsTouchDevice();
  // Only meaningful under the container's narrow rung: the folded search
  // trigger sets it, and the field clears it when it closes.
  const [searchExpanded, setSearchExpanded] = useState(false);
  const collapseSearch = useCallback(() => setSearchExpanded(false), []);
  // While the search field holds focus, a click-away layer covers the map:
  // the press that leaves the search is spent on leaving it — it must not
  // also open the pin or the cluster it happened to land on. The next press
  // reaches the map as usual. The layer stays until the press that started
  // on it has ENDED: its pointerdown blurs the field (which is the dismissal,
  // so the field needs no handle), and the field's blur would otherwise
  // remove the layer before the pointerup and click, which would then land
  // on the map. `click` is the last event of the press; a press that never
  // clicks (a pan, a cancelled touch) releases on the next tick instead.
  const [searchActive, setSearchActive] = useState(false);
  const [clickAwayHeld, setClickAwayHeld] = useState(false);
  const releaseTimer = useRef<number | null>(null);
  const releaseClickAway = useCallback(() => {
    if (releaseTimer.current != null) window.clearTimeout(releaseTimer.current);
    releaseTimer.current = null;
    setClickAwayHeld(false);
  }, []);
  const releaseClickAwayAfterPress = useCallback(() => {
    if (releaseTimer.current != null) window.clearTimeout(releaseTimer.current);
    releaseTimer.current = window.setTimeout(releaseClickAway, 0);
  }, [releaseClickAway]);
  // Except over the open pin's infobox: a press on a project card there is
  // aimed at the card, and the layer would spend it.
  const passThrough = useClickAwayPassThrough(searchActive || clickAwayHeld);
  useEffect(
    () => () => {
      if (releaseTimer.current != null)
        window.clearTimeout(releaseTimer.current);
    },
    [],
  );
  // Desktop actions sit on the default 32px `icon` rung — the same height as
  // the search field and the rail's workspace tile. Mobile keeps touch-sized
  // targets.
  const controlSize = isMobile ? "icon-xl" : undefined;
  const glyphSize = isMobile ? "lg" : undefined;
  const filterCount = filters.activeFilterCount;
  const filterLabel =
    filterCount > 0 ? `Filters, ${filterCount} active` : "Filters";
  // Tooltip copy matches the same three actions on the list and board
  // toolbars, so the map reads as one more view of the same tools. Desktop
  // opens them below the bar into open map; mobile's cluster hangs under
  // the search with a thumb over it, so its held tooltip opens above,
  // where the hand is not.
  const optionTooltipSide = isMobile ? "top" : "bottom";

  // Desktop joins a cluster into one seamless 32px bar (exactly the search
  // field's outer height); mobile keeps the padded touch dock.
  const cluster = (
    actions: React.ReactElement[],
    orientation: "horizontal" | "vertical",
  ) =>
    isMobile ? (
      <ControlBar
        backgroundColor={controlBg}
        borderColor={controlBorder}
        orientation={orientation}
        presentation="dock"
      >
        {actions}
      </ControlBar>
    ) : (
      <ControlBar backgroundColor={controlBg} borderColor={controlBorder}>
        <Group orientation={orientation}>
          {actions.flatMap((action, index) =>
            index === 0
              ? [action]
              : [
                  <Separator
                    key={`separator-${action.key}`}
                    orientation={
                      orientation === "vertical" ? "horizontal" : "vertical"
                    }
                  />,
                  action,
                ],
          )}
        </Group>
      </ControlBar>
    );

  const navigationActions = [
    ...(onLocationClick
      ? [
          <ControlBarButton
            key="locate"
            label="Center on my location"
            onClick={onLocationClick}
            shortcut={MAP_CENTER_SHORTCUT}
            size={controlSize}
            tooltipSide="right"
          >
            <Icon name="navigation" size={glyphSize} />
          </ControlBarButton>,
        ]
      : []),
    ...(isTouch
      ? []
      : [
          <ControlBarButton
            disabled={zoomLevel >= MAX_ZOOM}
            key="zoom-in"
            label="Zoom in"
            onClick={onZoomIn}
            shortcut={MAP_ZOOM_IN_SHORTCUT}
            size={controlSize}
            tooltipSide="right"
          >
            <Icon name="plus" size={glyphSize} />
          </ControlBarButton>,
          <ControlBarButton
            disabled={zoomLevel <= MIN_ZOOM}
            key="zoom-out"
            label="Zoom out"
            onClick={onZoomOut}
            shortcut={MAP_ZOOM_OUT_SHORTCUT}
            size={controlSize}
            tooltipSide="right"
          >
            <Icon name="minus" size={glyphSize} />
          </ControlBarButton>,
        ]),
  ];

  const optionActions = [
    // The panel renders the button and hangs off it. In the DOM the cluster
    // holds only the button — the menu is portalled — so the joined bar reads
    // the same as its siblings.
    <FilterPanel
      key="filters"
      open={filterPanelOpen}
      onOpenChange={onFilterPanelOpenChange}
      config={filterConfig}
      filters={filters}
      trigger={
        <ControlBarButton
          // The active-filter count is CONTENT of this segment, not a sticker
          // on its corner: the segment grows a numeral beside its glyph in its
          // own selected ink, so nothing hangs over the joined bar's seam onto
          // the neighbouring action. The bar is right-anchored, so the growth
          // runs leftward into open canvas and moves no sibling. The count
          // also rides the accessible name (`filterLabel`).
          expanded={filterCount > 0}
          label={filterLabel}
          selected={filterCount > 0}
          size={controlSize}
          tooltip="Quickly filter your Projects"
          tooltipSide={optionTooltipSide}
        >
          <Icon name="filter" size={glyphSize} />
          {/* Always mounted: the slot collapses by animating its width, and
              an unmounted slot has no width to animate from. */}
          <ButtonExpansion>
            {filterCount > 0 ? (
              <Text size="xs" tabular weight="medium">
                {filterCount}
              </Text>
            ) : null}
          </ButtonExpansion>
        </ControlBarButton>
      }
    />,
    <ControlBarButton
      key="only-me"
      label="Only me"
      onClick={currentUserSelected ? onCurrentUserClear : onCurrentUserClick}
      selected={currentUserSelected}
      size={controlSize}
      tooltip="Filter for your Projects"
      tooltipSide={optionTooltipSide}
    >
      {/* The me-mode mark everywhere is the current user's own avatar as a
        * 16px glyph, on the same icon line as the neighbouring svg glyphs —
        * the same face the toolbar's CurrentUserButton wears. */}
      <EntityTag
        display="visual"
        entity={{
          kind: "user",
          id: currentUser?.userId ?? "me",
          user: currentUser ?? null,
        }}
        interactive={false}
        size="2xs"
      />
    </ControlBarButton>,
    <ControlBarButton
      key="display-options"
      label="Customize view"
      onClick={onDisplayOptionsClick}
      selected={displayOptionsOpen}
      // The panel closes on a press on the page; this toggle's own press is
      // left to it, so it closes once rather than closing and reopening.
      {...OPTIONS_PANEL_TRIGGER_PROPS}
      size={controlSize}
      tooltip="Customize your view settings"
      tooltipSide={optionTooltipSide}
    >
      <Icon name="settings" size={glyphSize} />
    </ControlBarButton>,
  ];

  return (
    // Full-map overlay: drags pass through to the map; the controls stay
    // interactive. No single class expresses both, so the whole layer is inert
    // and each interactive cluster re-arms itself with `pointer-events-auto`.
    <Box
      pointerEvents="none" bottom={0}
      left={0}
      position="absolute"
      right={0}
      top={0}
    >
      {(searchActive || clickAwayHeld) && (
        <Box
          aria-hidden
          bottom={0}
          data-slot="map-search-click-away"
          left={0}
          onClick={releaseClickAway}
          onPointerCancel={releaseClickAwayAfterPress}
          onPointerDown={() => setClickAwayHeld(true)}
          onPointerUp={releaseClickAwayAfterPress}
          pointerEvents={passThrough ? "none" : "auto"}
          position="absolute"
          right={0}
          top={0}
          touchAction="none"
        />
      )}
      {/* Search and navigation — top left. Mobile's site header lives in the
        * drawer, so its shell trigger rides INSIDE the search field's start
        * cap: one island on the top row, not a button beside a field. On
        * desktop the options cluster shares the search's row so flex settles
        * the two against each other: the search grows to its 420px cap and
        * shrinks first when the visible map (right of an open drawer) cannot
        * hold both — a fixed reserve had let the cluster, which grows by its
        * filter count and shifts with the drawer, land on top of the field.
        * Narrower than a field beside the cluster, the field folds to a
        * glyph button (NARROW_HIDDEN_CLASS / WIDE_HIDDEN_CLASS swap the two)
        * and opens over the row on press. */}
      <Box
        align="start"
        className={`${TOP_INSET} ${isMobile ? "" : CONTROLS_CONTAINER_CLASS} pointer-events-none`}
        direction="column"
        gap="sm"
        left={16}
        position="absolute"
        right={16 + (isMobile ? 0 : rightInset)}
      >
        <Box
          align="center"
          direction="row"
          fill="width"
          gap="sm"
          justify="between"
          pointerEvents="none"
        >
          {!isMobile && !searchExpanded && (
            <Box className={WIDE_HIDDEN_CLASS} pointerEvents="auto">
              {cluster(
                [
                  <ControlBarButton
                    key="search"
                    label="Search Map"
                    onClick={() => setSearchExpanded(true)}
                  >
                    <Icon name="search" />
                  </ControlBarButton>,
                ],
                "horizontal",
              )}
            </Box>
          )}
          <Box
            align="center"
            className={
              isMobile || searchExpanded ? undefined : NARROW_HIDDEN_CLASS
            }
            direction="row"
            gap="sm"
            grow
            minWidth={0}
            pointerEvents="auto"
            width={isMobile ? "calc(100vw - 32px)" : undefined}
            maxWidth={isMobile ? undefined : 420}
          >
            <Box grow minWidth={0}>
              <MapSearchBar
                autoFocus={searchExpanded}
                backgroundColor={controlBg}
                borderColor={controlBorder}
                onActiveChange={setSearchActive}
                onCollapse={collapseSearch}
                onLocationSelect={onSearchLocationSelect}
                onProjectSelect={onSearchProjectSelect}
                startAction={
                  isMobile ? (
                    // The icon rung one step under the field's 2xl.
                    <AppShellTrigger shape="square" size="icon-xl" />
                  ) : undefined
                }
              />
            </Box>
          </Box>
          {!isMobile && (
            <Box
              className={searchExpanded ? NARROW_HIDDEN_CLASS : undefined}
              pointerEvents="auto"
              shrink={false}
            >
              {cluster(optionActions, "horizontal")}
            </Box>
          )}
        </Box>
        {/* Nothing to hold (touch, and no location fix yet) = no empty dock. */}
        {navigationActions.length > 0 && (
          <Box pointerEvents="auto">
            {cluster(navigationActions, "vertical")}
          </Box>
        )}
      </Box>

      {/* Mobile options cluster — its own second row, top right. */}
      {isMobile && (
        <Box
          className={`${MOBILE_SECOND_ROW_INSET} pointer-events-auto`}
          position="absolute"
          right={16}
        >
          {cluster(optionActions, "horizontal")}
        </Box>
      )}
    </Box>
  );
});
